/**
 * LayoutContext
 * Provides centralized layout configuration based on mode
 */

import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from './AuthContext';
import { layoutRegistry } from '@/lib/layout/registry';
import type { LayoutMode, LayoutContextValue, ChromeComponent } from '@/lib/layout/types';

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { appMode } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  // Priority: env var > appMode > default
  const mode: LayoutMode = useMemo(() => {
    const envMode = import.meta.env.REACT_APP_LAYOUT_MODE as LayoutMode | undefined;
    if (envMode && layoutRegistry[envMode]) return envMode;
    if (appMode) return 'app';
    return 'default';
  }, [appMode]);

  const config = layoutRegistry[mode];

  // Route enforcement
  useEffect(() => {
    if (!config.routes?.allowed) return;

    const isAllowed = config.routes.allowed.some(r =>
      location.pathname === r || location.pathname.startsWith(r)
    );

    if (!isAllowed && config.routes.redirectTo) {
      navigate(config.routes.redirectTo, { replace: true });
    }
  }, [mode, location.pathname, config.routes, navigate]);

  const shouldShow = (component: ChromeComponent): boolean => {
    return config.chrome[component];
  };

  const value: LayoutContextValue = {
    mode,
    config,
    shouldShow,
    styles: config.styles,
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
